import { Link, useNavigate } from "react-router-dom";
import { Menu, LogOut, User, Shield } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

export function GoogleLoginButton({ className = "" }) {
  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/profile";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button
      data-testid="google-login-button"
      onClick={handleLogin}
      className={`btn-sunset rounded-full px-6 ${className}`}
    >
      Sign in with Google
    </Button>
  );
}

const NAV_LINKS = [
  { label: "Home", href: "/#hero", testid: "nav-link-home" },
  { label: "Live Stats", href: "/#server-stats", testid: "nav-link-stats" },
  { label: "Rules", href: "/#rules", testid: "nav-link-rules" },
  { label: "Members", href: "/#members", testid: "nav-link-members" },
];

export default function Navbar() {
  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50 glass-panel border-x-0 border-t-0" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-brand">
          <img src="/logo.png" alt="Southside Roleplay logo" className="h-12 w-12 rounded-xl border border-sunset-amber/40" />
          <div className="leading-tight">
            <span className="font-display font-bold text-xl tracking-wide text-cream block">SOUTHSIDE</span>
            <span className="text-[10px] font-semibold uppercase tracking-[0.3em] text-sunset-amber">Roleplay · FiveM</span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <a
              key={l.testid}
              href={l.href}
              data-testid={l.testid}
              className="text-sm font-medium text-cream/70 hover:text-sunset-amber transition-colors"
            >
              {l.label}
            </a>
          ))}
          <Link
            to="/store"
            data-testid="nav-link-store"
            className="text-sm font-medium text-cream/70 hover:text-sunset-amber transition-colors"
          >
            Store
          </Link>
          <Link
            to="/apply"
            data-testid="nav-link-apply"
            className="text-sm font-semibold text-sunset-amber hover:text-sunset-gold transition-colors"
          >
            Apply to Join
          </Link>
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {isAdmin && (
            <Button
              data-testid="admin-nav-button"
              variant="outline"
              onClick={() => navigate("/admin")}
              className="btn-outline-amber rounded-full bg-transparent"
            >
              <Shield className="h-4 w-4 mr-2" /> Admin
            </Button>
          )}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-profile-menu" className="flex items-center gap-2 rounded-full border border-sunset-amber/40 pl-1 pr-3 py-1 hover:bg-sunset-amber/10 transition-colors">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user.picture} alt={user.name} />
                    <AvatarFallback className="bg-plum-600 text-cream text-xs">{user.name?.[0] || "U"}</AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium text-cream max-w-[120px] truncate">{user.name?.split(" ")[0]}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-plum-800 border-sunset-amber/30 text-cream">
                <DropdownMenuItem data-testid="menu-my-profile" onClick={() => navigate("/profile")} className="cursor-pointer focus:bg-sunset-amber/15">
                  <User className="h-4 w-4 mr-2" /> My Profile
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-sunset-amber/20" />
                <DropdownMenuItem data-testid="auth-logout-button" onClick={handleLogout} className="cursor-pointer text-red-400 focus:bg-red-500/10">
                  <LogOut className="h-4 w-4 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <GoogleLoginButton />
          )}
        </div>

        <div className="md:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-button" className="p-2 text-cream">
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-plum-800 border-sunset-amber/30 text-cream w-72">
              <div className="flex flex-col gap-5 mt-8">
                {NAV_LINKS.map((l) => (
                  <a key={l.testid} href={l.href} data-testid={`${l.testid}-mobile`} className="text-base font-medium text-cream/80">
                    {l.label}
                  </a>
                ))}
                <Link to="/apply" data-testid="nav-link-apply-mobile" className="text-base font-semibold text-sunset-amber">
                  Apply to Join
                </Link>
                <Link to="/store" data-testid="nav-link-store-mobile" className="text-base font-medium text-cream/80">
                  Store
                </Link>
                {isAdmin && (
                  <Link to="/admin" data-testid="admin-nav-button-mobile" className="text-base font-semibold text-sunset-amber">
                    Admin Dashboard
                  </Link>
                )}
                <div className="pt-4 border-t border-sunset-amber/20">
                  {user ? (
                    <Button data-testid="auth-logout-button-mobile" onClick={handleLogout} variant="outline" className="btn-outline-amber w-full bg-transparent">
                      <LogOut className="h-4 w-4 mr-2" /> Log out
                    </Button>
                  ) : (
                    <GoogleLoginButton className="w-full" />
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
